// Booking-length and pricing rules for the listing booking form.
// Kept free of React so they can be unit tested (hirePricing.test.js).

// Card holds expire after ~7 days, so bookings are capped at 5 (matches backend)
export const MAX_BOOKING_DAYS = 5
export const MAX_SAME_DAY_HOURS = 23
// Same floor as the $10 minimum hourly rate owners can set
export const MIN_BOOKING_PRICE = 10

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

// 4 → "4 hours", 48 → "2 days", 168 → "1 week"
export const formatHirePeriod = (hours) => {
  const h = Math.max(1, parseInt(hours) || 1)
  if (h % 168 === 0) return plural(h / 168, 'week')
  if (h % 24 === 0) return plural(h / 24, 'day')
  return plural(h, 'hour')
}

export const getBookingLimits = (listing) => {
  const minHours = Math.max(1, parseInt(listing?.min_hours) || 1)
  const minDays = Math.ceil(minHours / 24)
  return {
    minHours,
    minDays,
    // Same-day bookings top out at 23 hours, so hide them when the minimum is longer
    allowSameDay: !!listing?.price_per_hour && minHours <= MAX_SAME_DAY_HOURS,
    bookableOnline: minDays <= MAX_BOOKING_DAYS,
  }
}

// fmt formats a dollar amount for display (formatCurrencyByCountry bound to the listing's country)
export const calculatePricing = ({ listing, bookingMode, days, sameDayHours, fmt = v => `$${v}` }) => {
  const hourly = Number(listing?.price_per_hour) || 0
  const daily = Number(listing?.price_per_day) || 0
  const weekly = Number(listing?.price_per_week) || 0
  let subtotal = 0
  let breakdown = ''

  if (bookingMode === 'sameday') {
    subtotal = hourly * sameDayHours
    breakdown = `${plural(sameDayHours, 'hour')} × ${fmt(hourly)}`
  } else {
    const weeks = Math.floor(days / 7)
    const rem = days % 7
    if (daily) {
      // Unchanged: whole weeks at the weekly rate (if set), other days at the daily rate
      const useWeeks = weekly && weeks > 0
      subtotal = useWeeks ? weeks * weekly + rem * daily : days * daily
      breakdown = useWeeks
        ? [`${plural(weeks, 'week')} × ${fmt(weekly)}`, rem ? `${plural(rem, 'day')} × ${fmt(daily)}` : null].filter(Boolean).join(' + ')
        : `${plural(days, 'day')} × ${fmt(daily)}`
    } else if (weekly) {
      // No daily rate: never derive one from weekly ÷ 7. Days left over after
      // whole weeks cost the hourly rate × 24, capped at one week's rate.
      const remByHour = hourly ? rem * 24 * hourly : Infinity
      const remCost = rem ? Math.min(remByHour, weekly) : 0
      subtotal = weeks * weekly + remCost
      const parts = []
      if (weeks) parts.push(`${plural(weeks, 'week')} × ${fmt(weekly)}`)
      if (rem) {
        parts.push(remCost === weekly
          ? `${plural(rem, 'day')} at the weekly rate (no daily rate set)`
          : `${plural(rem, 'day')} × 24 hrs × ${fmt(hourly)}`)
      }
      breakdown = parts.join(' + ')
    } else if (hourly) {
      subtotal = days * 24 * hourly
      breakdown = `${plural(days, 'day')} × 24 hrs × ${fmt(hourly)} (no daily rate set)`
    }
  }

  subtotal = Math.round(subtotal * 100) / 100
  if (subtotal > 0 && subtotal < MIN_BOOKING_PRICE) {
    subtotal = MIN_BOOKING_PRICE
    breakdown += ` (minimum booking ${fmt(MIN_BOOKING_PRICE)})`
  }
  return { subtotal, breakdown }
}
